import { useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { TbFileSearch } from "react-icons/tb";
import { Dimmer, Grid, Loader, Placeholder, Table, TableRow } from "semantic-ui-react";
import {
  useCheckIngredientInMapping,
  useDeleteIngredient,
  useGetIngredientList,
  usePutSupplierData
} from "../../../api/ingredients";
import PlusIcon from "../../../assets/commonIcon/PlusIcon";
import { CommonTable, CustomButton, InputText, TitleBar } from "../../../components";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import PaginationView from "../../../components/pagination/Pagination";
import { DISABLE_BOM_CHEMICAL, IngredientTableColView } from "../../../config/constants";
import {
  IngredientDataType,
  IngredientDataTypeLabel
} from "../../../config/enums/IngredientType.enum";
import { SupplierType } from "../../../config/enums/SupplierType.enum";
import { ROLES } from "../../../config/permission-maps";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../utils/cacheStorage";
import IngredientDetailsModal from "../ingredientDetails/IngredientDetailsModal";
import IngredientEditModal from "../ingredientEdit/IngredientEditModal";
import IngredientRegisterModalView from "../ingredientRegister/IngredientRegisterModalView";
import IngredientFilter from "./IngredientFilter";

const IngredientList = () => {
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [showDeleteIngredientModal, setShowDeleteIngredientModal] = useState(false);
  const [viewIngredientData, setViewIngrdientData] = useState();
  const [editIngredientData, setEditIngrdientData] = useState();
  const [deleteIngredientData, setDeleteIngrdientData] = useState<any>({});
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [searchKey, setSearchKey] = useState("");
  const [filters, setFilters] = useState<{
    ingredientIds: string[];
    supplierIds: string[];
    dataTypes: string[];
    sortBy: number;
  }>({
    ingredientIds: [],
    supplierIds: [],
    dataTypes: [],
    sortBy: 3
  });

  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const { register, watch } = useForm();
  const [searchIngredient] = watch(["searchIngredient"]);

  const {
    data: ingredientList,
    isLoading: isIngredientListLoading,
    refetch: refetchIngredients
  } = useGetIngredientList(pageNumber, pageLimit, searchIngredient, filters);

  const refetchWithFilters = (
    newIngredientIds: string[],
    newSupplierIds: string[],
    newDataTypeIds: string[],
    status: number
  ) => {
    setFilters({
      ingredientIds: newIngredientIds,
      supplierIds: newSupplierIds,
      dataTypes: newDataTypeIds,
      sortBy: status
    });
  };

  const { isLoading, mutateAsync: mutatePutSupplier } = usePutSupplierData();

  const { isLoading: isIngredientInMappingLoading, mutate: isIngredientInMapping } =
    useCheckIngredientInMapping();

  const { isLoading: isIngredientDeleteLoading, mutate: deleteIngredient } = useDeleteIngredient();

  const handlePagination = (currentPage: any) => {
    setPageNumber(currentPage);
  };

  const handlePageSize = (pageSize: any) => {
    setPageLimit(pageSize);
  };

  const getIngredientDetails = (selectedIngredient: any) => {
    setViewIngrdientData(selectedIngredient);
    setVisibleDetailsModal(true);
  };

  const getIngredientForEditDetails = (detailsToEdit: any) => {
    setEditIngrdientData(detailsToEdit);
    setVisibleEditModal(true);
  };

  const getIngredientToDelete = (detailsToEdit: any) => {
    setDeleteIngrdientData(detailsToEdit);
    setShowDeleteIngredientModal(true);
  };

  const checkIngredientInMapping = () => {
    isIngredientInMapping(
      {
        ingredientId: deleteIngredientData?.ingredient_id,
        supplierId: deleteIngredientData?.supplier_id
      },
      {
        onSuccess: (exists: boolean) => {
          if (exists) {
            errorView("Ingredient is used in drawing tool, Please remove it first");
            setShowDeleteIngredientModal(false);
            return;
          }
          handleIngredientDelete();
        }
      }
    );
  };

  const handleIngredientDelete = () => {
    deleteSupplier();
    deleteIngredient(deleteIngredientData, {
      onSuccess() {
        setDeleteIngrdientData({});
        refetchIngredients();
      }
    });
    setShowDeleteIngredientModal(false);
  };

  const navigateDetailsToEdit = () => {
    setVisibleDetailsModal(false);
    setVisibleEditModal(true);
    getIngredientForEditDetails(viewIngredientData);
  };

  const deleteSupplier = () => {
    let supplierIngredient = deleteIngredientData.supplierIngredientIds.filter(
      (e: any) => e != deleteIngredientData.ingredient_id
    );
    let supplierUpdate = {
      _id: deleteIngredientData.supplier_id,
      supplier_name: deleteIngredientData.supplier_name,
      ingredientIds: supplierIngredient ? supplierIngredient : []
    };
    mutatePutSupplier(supplierUpdate, {
      onSuccess() {
        successMessage("Supplier removed from ingredient successfully");
        setDeleteIngrdientData({});
      }
    });
  };

  const getClimateImpactMessage = (dataView: any) => {
    const climateImpact = dataView?.supplierIngredientDetails?.climate_impact;
    const isSupplierData =
      dataView?.supplierIngredientDetails?.data_type === IngredientDataType.SUPPLIER;
    if (climateImpact) {
      if (checkIsUserSupplier) {
        return "Calculated: Upgrade to see results";
      }
      return climateImpact.toString().replace(/\s*kgCO2eq\s*$/, "");
    }
    return isSupplierData ? "Supplier information pending" : "Data collection in progress";
  };

  const loadTableData = () => {
    if (isIngredientListLoading) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <Table.Cell colSpan={7}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </TableRow>
          ))}
        </>
      );
    }
    const ingredientData = ingredientList?.result?.map((ingredient: any) => {
      return {
        ingredient_id: ingredient?._id,
        ingredient_name: ingredient?.ingredient_name,
        supplier_id: ingredient?.suppliers?._id || null,
        supplier_name: ingredient?.suppliers?.supplier_name,
        supplier_type: ingredient?.suppliers?.supplier_type || SupplierType.External,
        supplierAsUser: ingredient?.suppliers?.is_supplier_as_user || false,
        supplierIngredientDetails:
          ingredient?.suppliers?.supplierIngredientDetails?.find(
            (ingDetail: any) => ingDetail?.ingredient_id === ingredient?._id
          ) || null,
        ingredientProductIds: ingredient?.productIds || [],
        supplierProductIds: ingredient?.suppliers?.productIds || [],
        supplierIngredientIds: ingredient?.suppliers?.ingredientIds || [],
        supplierCount: ingredient?.supplierCount || 0,
        ingredient_image_path: ingredient?.ingredient_image_path
      };
    });

    return (
      <>
        {ingredientData?.map((dataView: any, index: number) => {
          let hideEditCol = dataView?.supplier_name ? false : true;
          return (
            <TableRow key={index}>
              <Table.Cell>{dataView?.ingredient_name}</Table.Cell>
              <Table.Cell>{dataView?.supplier_name || ""}</Table.Cell>
              <Table.Cell>
                {dataView?.supplierIngredientDetails?.unit_size || ""}
                {dataView?.supplierIngredientDetails?.unit_type || ""}
              </Table.Cell>
              <Table.Cell>{getClimateImpactMessage(dataView)}</Table.Cell>
              <Table.Cell>
                {IngredientDataTypeLabel[
                  dataView?.supplierIngredientDetails
                    ?.data_type as keyof typeof IngredientDataTypeLabel
                ] || ""}
              </Table.Cell>
              <Table.Cell>{dataView?.supplierIngredientDetails?.ean_bar_code || ""}</Table.Cell>
              <Table.Cell>
                {!hideEditCol ? (
                  <Grid>
                    <Grid.Column
                      computer={4}
                      tablet={5}
                      mobile={5}
                      className="align-content-center"
                    >
                      <TbFileSearch
                        cursor={"pointer"}
                        onClick={() => {
                          getIngredientDetails(dataView);
                        }}
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={4}
                      tablet={5}
                      mobile={5}
                      className="align-content-center"
                    >
                      <MdModeEditOutline
                        cursor={"pointer"}
                        onClick={() => {
                          getIngredientForEditDetails(dataView);
                        }}
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                    <Grid.Column
                      computer={4}
                      tablet={5}
                      mobile={5}
                      className="align-content-center"
                    >
                      <MdDeleteOutline
                        cursor={"pointer"}
                        onClick={() => {
                          getIngredientToDelete(dataView);
                        }}
                        size={24}
                        color="var(--tableEditIcon)"
                      />
                    </Grid.Column>
                  </Grid>
                ) : null}
              </Table.Cell>
            </TableRow>
          );
        })}
      </>
    );
  };

  // Loading state check
  if (isIngredientInMappingLoading || isLoading || isIngredientDeleteLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      {DISABLE_BOM_CHEMICAL ? <TitleBar titleName={"Ingredients"} /> : null}
      <Grid centered>
        <Grid.Column computer={4}>
          <InputText
            register={register}
            name="searchIngredient"
            placeholder="Search Ingredient"
            defaultValues={searchKey}
            onChangeFunction={(e: any) => {
              setSearchKey(e.target.value);
            }}
          />
        </Grid.Column>
        <Grid.Column computer={2}>
          <IngredientFilter refetchWithFilters={refetchWithFilters} />
        </Grid.Column>
        <Grid.Column computer={10} textAlign="right">
          <CustomButton
            theme="green"
            title="New Ingredient"
            onClick={() => setVisibleModal(true)}
            iconWithText={true}
            iconChild={<PlusIcon />}
          />
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable
            tableHeaderData={IngredientTableColView}
            singleLine={true}
            enableTableFooter={true}
            tableFooterChild={
              <PaginationView
                changePageSize={handlePageSize}
                pageSize={pageLimit}
                totalPages={Math.ceil((ingredientList?.total || 0) / pageLimit) || 1}
                currentPageNo={pageNumber}
                currentSelectPage={handlePagination}
              />
            }
          >
            {loadTableData()}
          </CommonTable>
        </Grid.Column>
      </Grid>
      {visibleModal ? (
        <IngredientRegisterModalView
          visibleModal={visibleModal}
          setVisibleModal={() => setVisibleModal(false)}
          ingredientList={ingredientList?.result || []}
          refetchIngredients={refetchIngredients}
        />
      ) : null}
      {visibleEditModal ? (
        <IngredientEditModal
          ingredientIdToUpdate={editIngredientData}
          visibleModal={visibleEditModal}
          setVisibleModal={() => setVisibleEditModal(false)}
          refetchIngredients={refetchIngredients}
          isEnableEnvironmentData={true}
          isEnableIngredientMapping={true}
        />
      ) : null}
      {visibleDetailsModal ? (
        <IngredientDetailsModal
          visibleModal={visibleDetailsModal}
          setVisibleModal={() => setVisibleDetailsModal(false)}
          ingredientDetails={viewIngredientData}
          navigateIngredientEdit={() => navigateDetailsToEdit()}
        />
      ) : null}
      <ConfirmModal
        viewModal={showDeleteIngredientModal}
        closeModal={() => setShowDeleteIngredientModal(false)}
        cancel={() => {
          setShowDeleteIngredientModal(false);
          setDeleteIngrdientData({});
        }}
        approve={() => {
          checkIngredientInMapping();
        }}
        title="Delete Ingredient Data"
        subTitle="Are you sure you want to remove this ingredient?"
      />
    </>
  );
};

export default IngredientList;
